import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  login as apiLogin,
  getGeneralInfo,
  updateUserOnboarding
} from './api';

import { consolidatedScoresAggregateQuery } from '../Instructor/insights/api';

import { getHighestActiveGame } from '../../utils';
import useAuth from '../../auth/useAuth';
import AuthService from '../../auth/AuthService';

import './styles.css';

export default function Login() {
  const navigate = useNavigate();
  const { 
    login,
    isUserAuthenticated,
    getUser,
    saveUser,
    isInstructor,
    isStudent,
    saveCurrentGameId,
    saveCurrentScenarioId
  } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Uses the ?login slim variant of /api/general-info/. Cache key
  // ['generalInfo', 'login'] is scoped to sign-in; downstream pages
  // (insights, game-play, assignments editor, etc.) fetch their own
  // full-payload variants on demand. `enabled: false` keeps the initial
  // mount idle — we fetch after successful login via refetchGeneralInfo.
  // retry: 0 because a 403 here is a real access issue, not a transient failure — retrying just spams logs.
  const { data: generalInfo, refetch: refetchGeneralInfo } = useQuery({
    queryKey: ['generalInfo', 'login'],
    queryFn: getGeneralInfo,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: false,
    retry: 0
  });

  // The redirect gate ("send instructor to insights the moment any
  // STUDENT completion exists") needs a student-only signal. The
  // ?login variant filters sessions to the caller, so we can't tell
  // student vs instructor ownership from it. This aggregate scores
  // endpoint is backend-filtered to completed student sessions and
  // exposes `unique_user_count` — a clean boolean signal without a
  // new field. Also pre-warms the cache the Insights → Overall page
  // reads from, so no double-fetch on the destination.
  const { data: aggregateScores, refetch: refetchAggregateScores } = useQuery({
    ...consolidatedScoresAggregateQuery({ completed: true }),
    enabled: false,
    retry: 0
  });

  // The full /course-members/ roster used to be fetched here just to
  // gate the post-login redirect on whether any student had completed
  // a session. We now infer that from generalInfo.sessions directly
  // (any completed session whose user isn't the logged-in instructor),
  // which avoids paginating a roster that can grow to hundreds of KB
  // on large courses.

  const navigateTo = useCallback(async () => {
    let user = getUser();
    const userIsInstructor = isInstructor();
    const userIsStudent = isStudent();

    let redirectUrl = '';
    const games = generalInfo?.games || [];
    const sessions = generalInfo?.sessions || [];

    // Onboarding bypass for returning instructors: a fresh signup gets
    // exactly one auto-seeded onboarding session, so an instructor with
    // onboarded=false but more than one session has clearly already used
    // the app (the assignment-creation tour just never fired to flip the
    // flag — e.g. they bypassed via direct URL, refreshed mid-tour, or
    // skipped the All Exercises button). Flip onboarded=true now so they
    // don't get stranded on the tutorial / level-points loop.
    if (userIsInstructor && user?.onboarded === false) {
      const userSessionCount = sessions.filter(s => s.user === user?.id).length;
      if (userSessionCount > 1) {
        try {
          await updateUserOnboarding({ id: user.id });
        } catch (err) {
          console.error('Failed to auto-bypass onboarding:', err);
        }
        const cached = AuthService.getUser();
        if (cached) {
          AuthService.setUser({ ...cached, onboarded: true });
          user = AuthService.getUser();
        }
      }
    }

    // Check if user needs onboarding based on the user record's `onboarded` flag.
    const needsOnboarding = user?.onboarded === false;

    if(userIsInstructor) {
      if(user?.onboarded) {
        // Redirect to insights the moment any STUDENT has completed a
        // session. unique_user_count comes from the aggregate scores
        // endpoint which is backend-filtered to completed student
        // sessions — a reliable student-only signal that ?login's
        // caller-scoped sessions array can't provide.
        const hasStudentData = (aggregateScores?.unique_user_count || 0) > 0;
        redirectUrl = hasStudentData
          ? '/instructor/insights/decision-insights/overall'
          : '/instructor/settings/assignments';
      } else {
        // User hasn't completed onboarding — check game session progress
        const userSessions = sessions.filter(s => s.user === user?.id);
        const instructorSession = userSessions[0];
        if(!instructorSession) {
          // No session: start from the Welcome onboarding page
          redirectUrl = '/instructor/onboarding';
        } else if(instructorSession.completed) {
          // Session completed: go to All Exercises page
          redirectUrl = '/instructor/onboarding/all-exercises';
        } else if(instructorSession.level2 === true || instructorSession.level2 === 'completed') {
          // Level 2 done but not completed: resume from where they left off
          redirectUrl = '/instructor/onboarding/all-exercises';
        } else if(instructorSession.level1 === true || instructorSession.level1 === 'completed') {
          // Level 1 done: go to level 2
          redirectUrl = '/classification/level/2?onboarding=true';
        } else {
          // Session exists but level 1 not done — only resume the
          // Classification tutorial when the session looks like a
          // genuine fresh-onboarding session: at most one session for
          // this user, incomplete, and tied to a Classification game
          // (the exact shape createDemoGame seeds during onboarding).
          // Established instructors with an abandoned preview session
          // on a non-Classification assignment fall through to the
          // assignments page instead of getting stranded on the
          // Classification tutorial that has no matching gameId in
          // localStorage.
          const sessionGame = games.find(g => g.id === instructorSession.game);
          const isClassificationGame = sessionGame?.game_type?.includes('Account Classification');
          const isFreshClassificationOnboarding =
            userSessions.length <= 1 &&
            !instructorSession.completed &&
            isClassificationGame;
          redirectUrl = isFreshClassificationOnboarding
            ? '/classification/level/1/tutorial?onboarding=true'
            : '/instructor/settings/assignments';
        }
      }
    } else if(userIsStudent) {
      if(needsOnboarding) {
        redirectUrl = '/onboarding';
      } else {
        if(user?.alias_name) {
          redirectUrl = '/assignments';
        } else {
          redirectUrl = '/onboarding/preferred-name';
        }
      }
    } else {
      redirectUrl = '/assignments';
    }
    navigate(redirectUrl);
  }, [generalInfo, aggregateScores, getUser, isInstructor, isStudent, navigate]);

  useEffect(() => {
    if (generalInfo?.users) {
      const users = Array.isArray(generalInfo.users) ? generalInfo.users : [generalInfo.users];
      const currentUser = users.find(user => user.username === username.toLowerCase());
      if (currentUser) {
        saveUser(currentUser);
      }
    }
  }, [generalInfo?.users, saveUser, username]);

  useEffect(() => {
    const user = getUser();
    // aggregateScores is instructor-only and skipped for students in onSuccess. Gate accordingly so students don't hang here waiting for a fetch that never fires.
    const aggregateScoresReady = user?.is_instructor ? !!aggregateScores : true;
    if (generalInfo?.games && generalInfo?.sessions && aggregateScoresReady && user) {
      const game = getHighestActiveGame(generalInfo.games);
      if(game) {
        saveCurrentGameId(game.id, game.game_type);
        saveCurrentScenarioId(game.scenario);
      }
      // During onboarding the tutorial page needs the Classification
      // game ID in localStorage, but getHighestActiveGame picks across
      // all game types. Select the highest-ID active Classification
      // game so the tutorial router can find it.
      if(user?.onboarded === false) {
        const classificationGame = getHighestActiveGame(
          generalInfo.games.filter(g => g.game_type?.includes('Account Classification'))
        );
        if(classificationGame) {
          saveCurrentGameId(classificationGame.id, classificationGame.game_type);
        }
      }
      navigateTo();
    }
  }, [generalInfo, aggregateScores, navigateTo, saveCurrentGameId, saveCurrentScenarioId, getUser]);

  const loginMutation = useMutation({
    mutationFn: (data) => apiLogin(data),
    onSuccess: ({ token, user }) => {
      login(token);
      // saveUser MUST run before the refetch — getGeneralInfo reads the stored user to pick /api/student/general-info/ vs /api/general-info/?login.
      if (user) saveUser(user);
      // Expired student: skip generalInfo / aggregate fetches (both 403) and route to the subscription-expired page. Instructors and assistants are unaffected.
      if (user?.is_student && user?.subscription?.is_active === false) {
        navigate('/acc/subscription-expired');
        return;
      }
      // Student: token now carries alias_name, onboarded, and subscription — everything Login needs to route. Skip the generalInfo fetch; Assignments will fetch its own on arrival.
      if (user?.is_student) {
        if (user?.onboarded === false) {
          navigate('/onboarding');
        } else if (user?.alias_name) {
          navigate('/assignments');
        } else {
          navigate('/onboarding/preferred-name');
        }
        return;
      }
      // Instructor path: still needs generalInfo (sessions/games for onboarding routing). Aggregate scores is instructor-only — assistants would 403 on it, so keep the explicit gate.
      refetchGeneralInfo();
      if (user?.is_instructor) refetchAggregateScores();
    },
    onError() {
      setWarningMessage('Login details are incorrect, please try again');
      setIsLoading(false);
    }
  });

  const handleLogin = () => {
    if (!navigator.onLine) {
      setWarningMessage('No internet connection, please check your internet and try again.');
      return;
    }
    if (username.trim() && password.trim()) {
      setIsLoading(true);
      loginMutation.mutate({ username: username.trim().toLowerCase(), password: password.trim() });
    } else {
      setWarningMessage('Please enter both username and password.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value.trim());
    
    setWarningMessage('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value.trim());
    
    setWarningMessage('');
  };
  

  return (
    <div className="page-content page-content-login">
      <div className="login-page-container">
        <h1>Master the language of business</h1>
        <h3>Fun <span className='red-highlight'>active learning</span> games for financial accounting courses </h3>
        <div className="form">
          <div className="form-row">
            <div className="input-container">
              <label>Username</label>
              <input 
                type="text"
                value={username}
                onChange={handleUsernameChange}
                onKeyUp={handleKeyPress}
                placeholder="Username"
              />
            </div>
            <div className="input-container">
              <label>Password</label>
              <input 
                type="password"
                value={password}
                onChange={handlePasswordChange}
                onKeyUp={handleKeyPress}
                placeholder="Password"
              />
            </div>
          </div>
          <div className="form-row">
            <button
              className={`button-red button-submit ${isLoading ? 'loading' : ''}`}
              onClick={handleLogin}
              disabled={isLoading}
            >
              {!isLoading && 'Login'}
            </button>
          </div>
          <div className="form-row">
            {warningMessage && <span className="login-warning">{warningMessage}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
